/* -----------------Globals--------------- */
import { useCallback } from "react";

/* -----------------Helpers & Hooks--------------- */
import useSessionStore from "./useSessionStore";

import { events } from "@constants/tracking";
import { useMutation } from "@tanstack/react-query";
import { trackCustomEvent } from "analytics";
/* -----------------Types--------------- */
import { useRouter } from "expo-router";
import { fetchSessionItems } from "../api";
import type { ResumeSessionRequest } from "../api/types";
import { findLastUpdatedItemIndex } from "../helpers/session";
import { flattenSessionItems } from "../helpers/session";

export const useResumeSession = () => {
	const router = useRouter();

	const resumeSessionMutation = useMutation({
		mutationFn: fetchSessionItems,
	});

	const resumeSession = useCallback(
		(request: ResumeSessionRequest) => {
			return resumeSessionMutation.mutateAsync(request);
		},
		[resumeSessionMutation],
	);
	const updateCurrentBlockIndex = useSessionStore(
		(state) => state.updateCurrentBlockIndex,
	);

	const updateProgress = useSessionStore((state) => state.updateProgress);
	const updateProgressForEachBlock = useSessionStore(
		(state) => state.updateProgressForEachBlock,
	);
	const updateSession = useSessionStore((state) => state.updateSession);
	const handleResumeSession = useCallback(
		async (numericId: number) => {
			const result = await resumeSession({
				sessionId: numericId,
			});
			const flattenedSessionItems = flattenSessionItems(result.sessionItems);
			const { lastUpdatedBlockIndex, lastUpdatedItemIndex } =
				findLastUpdatedItemIndex(flattenedSessionItems);

			const { status } = result;

			const sessionResponse = {
				...result,
				sessionItems: flattenedSessionItems,
			};

			if (status === "COMPLETE") {
				router.replace(`/session/${numericId}/report/`);
			} else {
				trackCustomEvent({
					eventName: events.sessionResumed,
					numericId,
				});
				updateCurrentBlockIndex(lastUpdatedBlockIndex);
				const numItems = flattenedSessionItems[lastUpdatedBlockIndex].length;

				updateSession({
					sessionResponse,
					numItemsRequested: numItems,
					isSummativeTest: result.sessionType !== "FORMATIVE",
					isDiagnostic: false,
				});
				if (lastUpdatedBlockIndex > 0) {
					for (let i = 0; i < lastUpdatedBlockIndex; i++) {
						updateProgressForEachBlock(1, i);
					}
				}

				const progress = (lastUpdatedItemIndex + 1) / numItems;

				updateProgress(progress);

				// for (const { id, progress } of progressArray) {
				// 	updateProgressForEachBlock(progress, Number(id));
				// }

				router.push({
					pathname: "/(protected)/session/[id]",
					params: {
						id: sessionResponse.numericId,
						index: String(lastUpdatedItemIndex) || 1,
					},
				});
			}
		},
		[
			router,
			updateCurrentBlockIndex,
			updateProgress,
			updateSession,
			resumeSession,
			updateProgressForEachBlock,
		],
	);

	return {
		handleResumeSession,
	};
};
